import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import EntityTableProvider from '../entity-table-provider/entity-table-provider.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';
import { BvModal } from 'bootstrap-vue';
import Component, { mixins } from 'vue-class-component';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import { Prop, Watch } from 'vue-property-decorator';
import AuthorizationHelper from '@auth/services/authorization.helper';
import EntityTable from '../entity-table/entity-table';
import PaginationInterface from '@core/interfaces/pagination-interface';
import { BPCDisplayConfigField } from '@core/interfaces/bpc-display-config';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import TableExportsToCSV from './table-exports-to-csv';
import ValidationWarning from './validation-warning';
import ExportToPosCheckout from '@shared/components/tables/abstract-table/export-to-pos-checkout';
import { PosSession } from '@pos/interfaces/pos-session';
import SearchAndOrder from '@core/interfaces/bpc-search-and-order';
import ExtendedFiltersMixin from './extended-filters-mixin';

@Component({
  name: 'bpc-abstract-table',
  components: {},
})
export default class AbstractTable extends mixins(
  TableExportsToCSV,
  ValidationWarning,
  ExportToPosCheckout,
  ExtendedFiltersMixin,
) {
  declare $refs: TableExportsToCSV['$refs'] &
    ValidationWarning['$refs'] &
    ExportToPosCheckout['$refs'] &
    ExtendedFiltersMixin['$refs'] & {
      // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
      stdForm: InstanceType<typeof StdForm> & {
        saveEntity(): Promise<BPCGenericApiResponse>;
        formModel: any;
        onDeleteMediaFile(): void;
        searchEntity(): void;
        extraData: string | AnyEntity;
        formProvider: EntityFormProvider;
        prepareToSave(entity: any): AnyEntity;
      };
      stdBulkForm: InstanceType<typeof StdForm> & {
        saveEntity(): Promise<BPCGenericApiResponse>;
        formModel: any;
        onDeleteMediaFile(): void;
        searchEntity(): void;
        extraData: string | AnyEntity;
        formProvider: EntityFormProvider;
      };
      stdFilter: InstanceType<typeof StdFilter> & {
        filterExcluded: string[];
      };
      tabStdFilter: InstanceType<typeof StdFilter> & {};
      provider: InstanceType<typeof EntityTableProvider> & {
        getEntities(): void;
        items: any[];
        fields: any[];
        searchAndOrder: SearchAndOrder;
        queryConfig: any;
        loadingURLQueryParams: boolean;
        loadingTabURLQueryParams: boolean;
        withTabFilter: boolean;
        canLoadEntities: boolean;
        firstLoadEntities: boolean;
        initialFilters: Record<string, any>[];
        pagination: PaginationInterface;
      };
      softDeleteConfirmationModal: InstanceType<typeof ConfirmationModal> & {
        onOpenModal(id): void;
        onCloseModal(): void;
      };
      deleteConfirmationModal: InstanceType<typeof ConfirmationModal> & {
        onOpenModal(id): void;
        onCloseModal(): void;
      };
      recoverConfirmationModal: InstanceType<typeof ConfirmationModal> & {
        onOpenModal(id): void;
        onCloseModal(): void;
      };
      bulkSoftDeleteConfirmationModal: InstanceType<typeof ConfirmationModal> & {
        onOpenModal(id): void;
        onCloseModal(): void;
      };
      bulkDeleteConfirmationModal: InstanceType<typeof ConfirmationModal> & {
        onOpenModal(id): void;
        onCloseModal(): void;
      };
      bulkRecoverConfirmationModal: InstanceType<typeof ConfirmationModal> & {
        onOpenModal(id): void;
        onCloseModal(): void;
      };
      deleteMediaFileConfirmationModal: InstanceType<typeof ConfirmationModal> & {
        onOpenModal(id): void;
        onCloseModal(): void;
      };
      entityTable: InstanceType<typeof EntityTable> & {};
    };

  @Prop({ type: String, default: 'table' }) readonly storeName: string;
  @Prop({ type: String, default: 'table' }) readonly displayConfigName: string;
  @Prop({ type: String, default: 'default' }) readonly filterConfigName: string;
  @Prop({ type: String, default: 'default' }) readonly formConfigName: string;
  @Prop({ type: String, default: 'cancel' }) readonly cancelFormConfigName: string;
  @Prop({ type: Array, default: null }) readonly selectConfigName: string[];
  @Prop(String) readonly configFileName: string;
  @Prop({ type: String, default: null }) readonly forcedInitialView: string;
  @Prop(Number) readonly forcedPaginationPerPage: string;
  @Prop({ type: Boolean, default: true }) readonly showTitle: boolean;
  @Prop({ type: Boolean, default: false }) readonly ignoreURLParams: boolean;
  @Prop({
    type: Array,
    default() {
      return [];
    },
  })
  readonly filters: any[];
  @Prop({ type: Boolean, default: false }) readonly disabled: boolean;
  @Prop({ type: Boolean, default: false }) readonly emitOnNew: boolean;
  @Prop({ type: Boolean, default: false }) readonly emitOnEdit: boolean;
  @Prop({ type: Boolean, default: false }) readonly emitOnBulkEdit: boolean;
  @Prop({ type: Boolean, default: true }) readonly withExtendedFilters: boolean;
  @Prop({ type: Boolean, default: true }) readonly withBadgeFilterList: boolean;
  @Prop({ type: String, default: 'extended' }) readonly extendedFilterConfigName: string;
  @Prop({ type: Boolean, default: false }) readonly showCategoryFilter: boolean;
  @Prop({ type: Boolean, default: false }) readonly isInput: boolean;
  @Prop({ type: Boolean, default: false }) readonly withTabFilters: boolean;
  @Prop(String) declare readonly title: string;
  @Prop(String) declare readonly subtitle: string;
  @Prop({
    type: Array,
    default: null,
  })
  readonly initialFlagsToCheck: string[];
  beforeMount() {
    if (this.initialFlagsToCheck) {
      this.flagsToCheckBeforeLoadEntities = this.initialFlagsToCheck;
    }
  }

  get bulkEditPath(): string {
    return `${this.$bpcApiRouteHelper.getEntityPath(this.pluralEntityName)}/bulk-edit`;
  }

  get canManageEntity(): boolean {
    return (
      (this.permissionEntityNameToUse || this.pluralEntityName) &&
      this.$authorizationService.hasPermissions([
        `manage_${
          this.permissionEntityNameToUse
            ? this.permissionEntityNameToUse.replace('-', '_')
            : this.pluralEntityName.replace('-', '_')
        }`,
        `manage_own_${
          this.permissionEntityNameToUse
            ? this.permissionEntityNameToUse.replace('-', '_')
            : this.pluralEntityName.replace('-', '_')
        }`,
      ])
    );
  }

  get openPosSession(): PosSession {
    return this.$store.getters['pos/openPosSession'];
  }

  $bvModal!: BvModal;

  public entity: any = null;
  public declare entityName: string;
  public pluralEntityName: string;
  public permissionEntityNameToUse: string = null;

  public config: any;

  public showForm: boolean = false;
  public showDrawerSection: boolean = false;
  public showBulkDrawerSection: boolean = false;
  public disabledForm = true; // For read only mode when click in code column

  public showBulkEditForm: boolean = false;
  public isSavingForm: boolean = false;
  public isSavingBulkForm: boolean = false;

  public initialView = null;
  public exportCSVModalId: string = this.generateUUIDv4();
  public searchAndOrderQueryParams: SearchAndOrder = null;

  i = 0;
  selection = [];

  public checkedAllEntities: boolean = false;

  currentAction: string = null;
  nextAction: string = null;

  public customPageActions = {};
  public defaultPageActions = {
    bulkDelete: {
      activated: false,
      type: 'action',
      icon: 'trash', //'times',
      variant: 'danger',
      text: 'delete',
      show: false,
      event: 'bulk-delete',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_destroy'],
    },
    bulkRecover: {
      activated: false,
      type: 'action',
      icon: 'undo',
      variant: 'info',
      text: 'recover',
      show: false,
      event: 'bulk-recover',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_recover'],
    },
    bulkSoftDelete: {
      activated: false,
      type: 'action',
      icon: 'trash',
      variant: 'danger',
      text: 'soft-delete',
      show: false,
      event: 'bulk-soft-delete',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_delete'],
    },
    bulkEdit: {
      activated: false,
      type: 'action',
      icon: 'edit',
      variant: 'info',
      text: 'bulk-edit',
      show: true,
      event: 'bulk-edit',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_edit'],
    },
    bulkPosCheckout: {
      activated: false,
      type: 'action',
      icon: 'calculator', //'times',
      variant: 'info',
      text: 'bulk-pos-checkout',
      show: false,
      event: 'bulk-pos-checkout',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_pos_checkout'],
    },
    export: {
      activated: false,
      type: 'action',
      icon: 'download', //'times',
      variant: 'info',
      text: 'export',
      show: true,
      event: 'export',
      selectElements: true,
      tag: 'middle',
      permissions: ['export'],
    },
    exportAll: {
      activated: false,
      type: 'action',
      icon: 'download', //'times',
      variant: 'info',
      text: 'export',
      show: false,
      event: 'export',
      selectElements: true,
      tag: 'middle',
      permissions: ['export'],
    },
    create: {
      activated: false,
      type: 'action',
      icon: 'plus',
      variant: 'success',
      text: 'create-new',
      show: true,
      event: 'create',
      selectElements: false,
      tag: 'right',
      permissions: ['store'],
    },
    import: {
      activated: false,
      type: 'action',
      icon: 'upload',
      variant: 'secondary',
      text: 'import',
      show: true,
      event: 'import',
      selectElements: false,
      tag: 'left',
    },
    // actions: {
    //   activated: false,
    //   type: 'dropdown',
    //   icon: null,
    //   variant: 'secondary',
    //   text: 'actions',
    //   show: true,
    //   selectElements: false,
    //   tag: 'left',
    //   children: {
    //     export: {
    //       type: 'head',
    //       text: 'export',
    //       show: true,
    //       withoutDivider: true,
    //     },
    //     export_full_list: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'export-full-list',
    //       event: 'export-full-list',
    //     },
    //     export_selection: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'export-selection',
    //       event: 'export-selection',
    //     },
    //     reporting: {
    //       type: 'head',
    //       text: 'reporting',
    //       show: true,
    //     },
    //     adeps_list: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'adeps-list',
    //       event: 'adeps-list',
    //     },
    //     club_list: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'clubs-list',
    //       event: 'clubs-list',
    //     },
    //     others: {
    //       type: 'head',
    //       show: true,
    //     },
    //     print: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'print',
    //       event: 'print',
    //     },
    //   },
    //},
    // export: {
    //   activated: false,
    //   type: 'dropdown',
    //   icon: null,
    //   variant: 'secondary',
    //   text: 'exporter',
    //   show: true,
    //   selectElements: false,
    //   tag: 'left',
    //   children: {
    //     export: {
    //       type: 'head',
    //       text: 'export',
    //       show: true,
    //       withoutDivider: true,
    //     },
    //     export_full_list: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'export-full-list',
    //       event: 'export-full-list',
    //     },
    //     export_selection: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'export-selection',
    //       event: 'export-selection',
    //     },
    //     reporting: {
    //       type: 'head',
    //       text: 'reporting',
    //       show: true,
    //     },
    //     adeps_list: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'adeps-list',
    //       event: 'adeps-list',
    //     },
    //     club_list: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'clubs-list',
    //       event: 'clubs-list',
    //     },
    //     others: {
    //       type: 'head',
    //       show: true,
    //     },
    //     print: {
    //       type: 'dropdown-item',
    //       show: true,
    //       text: 'print',
    //       event: 'print',
    //     },
    //   },
    // },
    settings: {
      activated: false,
      type: 'action',
      icon: 'cog',
      variant: 'secondary',
      text: null,
      show: true,
      event: 'settings',
      selectElements: false,
      tag: 'right',
    },
    help: {
      activated: false,
      type: 'action',
      icon: 'question',
      variant: 'secondary',
      text: null,
      show: true,
      event: 'help',
      selectElements: false,
      tag: 'right',
    },
  };
  get pageActions() {
    return {
      ...this.defaultPageActions,
      ...this.customPageActions,
    };
  }

  public customRowActions = {};
  public defaultRowActions = {
    posCheckout: {
      activated: false,
      type: 'action',
      icon: 'calculator',
      show: false,
      event: 'pos-checkout',
      text: 'pos-checkout',
      permissions: ['pos_checkout'],
    },
    cancel: {
      activated: false,
      type: 'action',
      icon: 'ban',
      show: false,
      event: 'cancel',
      text: 'cancel',
      permissions: ['cancel'],
    },
    edit: {
      activated: false,
      type: 'action',
      icon: 'edit',
      show: false,
      event: 'edit',
      text: 'edit',
      permissions: ['update'],
    },
    softDelete: {
      activated: false,
      type: 'action',
      icon: 'trash',
      show: false,
      event: 'soft-delete',
      text: 'soft-delete',
      permissions: ['delete'],
    },
    delete: {
      activated: false,
      type: 'action',
      icon: 'trash', //'times',
      show: false,
      event: 'delete',
      text: 'delete',
      permissions: ['destroy'],
    },
    recover: {
      activated: false,
      type: 'action',
      icon: 'undo',
      show: false,
      event: 'recover',
      text: 'recover',
      permissions: ['recover'],
    },
    select: {
      activated: false,
      type: 'action',
      icon: 'hand-pointer',
      show: true,
      event: 'select',
      text: 'select',
    },
    show: {
      activated: false,
      type: 'action',
      icon: 'eye',
      show: false,
      event: 'show',
      text: 'show',
      permissions: ['show'],
    },
  };

  // Default Flags to check before load entities in the table.
  public flagsToCheckBeforeLoadEntities: string[] = [
    'configLoaded',
    'paginationLoaded',
    'orderLoaded',
    'urlParametersLoaded',
  ];

  get rowActions() {
    return {
      ...this.defaultRowActions,
      ...this.customRowActions,
    };
  }

  get filterModel() {
    return this.$store.getters[`${this.storeName}/filterModel`](this.entityName);
  }

  public displayFieldsToSubscribe = ['code', 'document_number'];

  destroyed() {
    if (this.$refs.entityTable) {
      this.$refs.entityTable.$off('on-click-display');
    }
  }

  /** Modals Config */
  getSoftDeleteConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: this.entityName + '-soft-delete-modal-' + this.generateUUIDv4(),
      title: this.$t('global.modals.soft-delete.title', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }),
      body: this.$t('global.modals.soft-delete.body', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.move_to_trash') as string,
      acceptButtonVariant: 'danger',
    };
  }

  getDeleteConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: this.entityName + '-delete-modal-' + this.generateUUIDv4(),
      title: this.$t('global.modals.delete.title', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }),
      body: this.$t('global.modals.delete.body', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.permanent_delete') as string,
      acceptButtonVariant: 'danger',
    };
  }
  getRecoverConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: this.entityName + '-recover-modal-' + this.generateUUIDv4(),
      title: this.$t('global.modals.recover.title', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }),
      body: this.$t('global.modals.recover.body', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.recover') as string,
      acceptButtonVariant: 'info',
    };
  }

  getBulkSoftDeleteConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: this.entityName + '-bulk-soft-delete-modal-' + this.generateUUIDv4(),
      title: this.$t('global.modals.bulk-soft-delete.title', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }),
      body: this.$t('global.modals.bulk-soft-delete.body', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.move_to_trash') as string,
      acceptButtonVariant: 'danger',
    };
  }

  getBulkDeleteConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: this.entityName + '-bulk-delete-modal-' + this.generateUUIDv4(),
      title: this.$t('global.modals.bulk-delete.title', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }),
      body: this.$t('global.modals.bulk-delete.body', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.permanent_delete') as string,
      acceptButtonVariant: 'danger',
    };
  }
  getBulkRecoverConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: this.entityName + '-bulk-recover-modal-' + this.generateUUIDv4(),
      title: this.$t('global.modals.bulk-recover.title', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }),
      body: this.$t('global.modals.bulk-recover.body', {
        entityName: this.$t('global.models.' + this.entityName + '.name'),
        entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
        thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
        thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.recover') as string,
      acceptButtonVariant: 'info',
    };
  }

  getDeleteMediaFileConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'media-file-delete-modal-' + this.generateUUIDv4(),
      title: this.$t('global.modals.media-delete.title') as string,
      body: this.$t('global.modals.media-delete.body') as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.permanent_delete') as string,
      acceptButtonVariant: 'danger',
    };
  }
  /**
   * End modal configs
   */

  onPageAction($event: string) {
    switch ($event) {
      case 'bulk-delete':
        this.onBulkDelete();
        break;
      case 'bulk-soft-delete':
        this.onBulkSoftDelete();
        break;
      case 'bulk-recover':
        this.onBulkRecover();
        break;
      case 'bulk-edit':
        this.onBulkEdit();
        break;
      case 'export':
        this.onExport();
        break;
      // case 'export-all':
      //   this.onExportAll();
      //   break;
      case 'create':
        this.onNew();
        break;
      case 'bulk-pos-checkout':
        this.onBulkExportToPosCheckout(
          this.$refs.provider.items.filter(item => this.selection.includes(item['id'])) || [],
        );
        break;
    }
    this.customOnPageAction($event);
    console.log('Page Title event >>> ' + $event);
  }
  customOnPageAction($event: string) {
    console.log('Custom Page Title event >>> ' + $event);
  }
  onRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'delete':
        this.onDelete($event.item);
        break;
      case 'soft-delete':
        this.onSoftDelete($event.item);
        break;
      case 'recover':
        this.onRecover($event.item);
        break;
      case 'edit':
        if ($event.item.item.actions.edit.activated) {
          this.onEdit($event.item);
        }
        break;
      case 'show':
        this.onShow($event.item);
        break;
      case 'select':
        this.onSelect($event.item);
        break;
      case 'pos-checkout':
        this.onExportToPosCheckout($event.item);
        break;
    }
    this.customOnRowAction($event);
    console.log('Row action event >>> ' + $event.event);
  }
  customOnRowAction($event: { event: string; item: any }) {
    console.log('Custom Row event >>> ' + $event.event);
  }

  onSoftDelete($event) {
    if (this.getMode() == 'storage') {
      this.$refs.softDeleteConfirmationModal.onOpenModal($event['item']['provisional_id']);
    } else {
      this.$refs.softDeleteConfirmationModal.onOpenModal($event['item']['id']);
    }
  }
  onSuccessSoftDelete() {
    this.$refs.softDeleteConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  onDelete($event) {
    this.$refs.deleteConfirmationModal.onOpenModal($event['item']['id']);
  }
  onSuccessDelete() {
    this.$refs.deleteConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  onRecover($event) {
    this.$refs.recoverConfirmationModal.onOpenModal($event['item']['id']);
  }
  onSuccessRecover() {
    this.$refs.recoverConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  onBulkSoftDelete() {
    this.$refs.bulkSoftDeleteConfirmationModal.onOpenModal(this.selection);
  }
  onSuccessBulkSoftDelete() {
    this.$refs.bulkSoftDeleteConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  onBulkDelete() {
    this.$refs.bulkDeleteConfirmationModal.onOpenModal(this.selection);
  }
  onSuccessBulkDelete() {
    this.$refs.bulkDeleteConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  onBulkRecover() {
    this.$refs.bulkRecoverConfirmationModal.onOpenModal(this.selection);
  }
  onSuccessBulkRecover() {
    this.$refs.bulkRecoverConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  onRefresh() {
    this.$refs.provider.getEntities();
  }
  showDeleteMediaFileModal(mediaId: string) {
    this.$refs.deleteMediaFileConfirmationModal.onOpenModal(mediaId);
  }
  onSuccessMediaFileDelete() {
    this.$refs.deleteMediaFileConfirmationModal.onCloseModal();
    this.getSelection();
    this.$refs.provider.getEntities();
  }
  getSelection() {
    const selection = [];
    this.checkedAllEntities = true;
    if (this.$refs.provider && this.$refs.provider.items) {
      this.$refs.provider.items.forEach(element => {
        if (element['checked']) {
          selection.push(element['id']);
        } else {
          this.checkedAllEntities = false;
        }
      });
    }
    this.selection = [...selection];
  }

  updatePageActions() {
    if (this.config) {
      for (const activatedPageAction of this.config.data.activatedPageActions) {
        if (this.pageActions[activatedPageAction]) {
          this.pageActions[activatedPageAction].activated = true;
        }
      }
    }
    this.pageActions.bulkDelete.show =
      this.$refs.provider &&
      (this.$refs.provider.queryConfig.params.status === 'trash' ||
        this.$refs.provider.queryConfig.params.onlyTrashed)
        ? true
        : false;
    this.pageActions.bulkRecover.show =
      this.$refs.provider &&
      (this.$refs.provider.queryConfig.params.status === 'trash' ||
        this.$refs.provider.queryConfig.params.onlyTrashed)
        ? true
        : false;
    this.pageActions.bulkSoftDelete.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed
        ? true
        : false;

    // POS Checkout
    const hasPosCheckoutPermission = this.$authorizationService.hasPermissionForAction(
      'pos_sessions',
      'bulk_pos_checkout',
      true,
    );
    this.pageActions.bulkPosCheckout.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$refs.provider.items?.find(item => item['orders']?.length > 0) &&
      this.$refs.provider.items?.find(item =>
        item['orders'].some(
          order =>
            order.status == 'waiting' ||
            (order.type === 'attendee' && order.status === 'shopping_cart'),
        ),
      ) &&
      hasPosCheckoutPermission &&
      !!this.openPosSession;

    // Check Permissions
    const activatedPageActions = this.config?.data?.activatedPageActions || [];
    for (const activatedPageAction of activatedPageActions) {
      if (
        this.pageActions[activatedPageAction] &&
        this.pageActions[activatedPageAction]['permissions']
      ) {
        this.checkPermission(activatedPageAction);
      }
    }
    this.extendUpdatePageActions();
  }
  extendUpdatePageActions() {
    return null;
  }
  checkPermission(activatedPageAction: string) {
    const permissions = [];
    this.pageActions[activatedPageAction]['permissions'].forEach(permissionName => {
      permissions.push(
        {
          name: AuthorizationHelper.getPermissionName(
            permissionName,
            (this.permissionEntityNameToUse
              ? this.permissionEntityNameToUse
              : this.pluralEntityName
            ).replace('-', '_'),
          ),
        },
        {
          name: AuthorizationHelper.getPermissionName(
            permissionName,
            (this.permissionEntityNameToUse
              ? this.permissionEntityNameToUse
              : this.pluralEntityName
            ).replace('-', '_'),
            true,
          ),
        },
      );
    });
    this.pageActions[activatedPageAction]['activated'] = this.$authorizationService.hasPermissions(
      permissions,
    );
  }

  tableIsNotFilteredByTrashedElements(): boolean {
    return !!(
      this.$refs.provider &&
      (Array.isArray(this.$refs.provider.queryConfig.params.status)
        ? !this.$refs.provider.queryConfig.params.status.includes('trash')
        : this.$refs.provider.queryConfig.params.status !== 'trash') &&
      !this.$refs.provider.queryConfig.params.onlyTrashed
    );
  }

  tableIsFilteredByTrashedElements(): boolean {
    return !!(
      (this.$refs.provider &&
        (Array.isArray(this.$refs.provider.queryConfig.params.status)
          ? this.$refs.provider.queryConfig.params.status.includes('trash')
          : this.$refs.provider.queryConfig.params.status === 'trash')) ||
      this.$refs.provider?.queryConfig.params.onlyTrashed
    );
  }

  updateRowActions() {
    if (this.config && this.$refs.provider.items) {
      // We match the rowActions with the config
      // We activate or deactivate depends on the status filter
      this.rowActions.delete.show = this.tableIsFilteredByTrashedElements();
      this.rowActions.recover.show = this.tableIsFilteredByTrashedElements();
      this.rowActions.softDelete.show = this.tableIsNotFilteredByTrashedElements();
      this.rowActions.show.show = this.tableIsNotFilteredByTrashedElements();
      this.rowActions.edit.show = this.tableIsNotFilteredByTrashedElements();

      // POS Checkout
      const hasPosCheckoutPermission = this.$authorizationService.hasPermissionForAction(
        'pos_sessions',
        'pos_checkout',
        true,
      );

      this.rowActions.posCheckout.show =
        this.tableIsNotFilteredByTrashedElements() &&
        hasPosCheckoutPermission &&
        !!this.openPosSession;

      for (const entity of this.$refs.provider.items) {
        entity.actions = JSON.parse(JSON.stringify(this.rowActions));
        // we determinate the authorization for each elements of the list. We do it this in the for becouse is neccesary for owner permissions
        Object.entries(entity.actions).forEach(element => {
          const actionConfig = element[1];
          const actionName = element[0];
          let hasPermissions = true;
          if (actionConfig['permissions']) {
            const permissions = [];
            actionConfig['permissions'].forEach(permissionName => {
              permissions.push(
                {
                  name: AuthorizationHelper.getPermissionName(
                    permissionName,
                    (this.permissionEntityNameToUse
                      ? this.permissionEntityNameToUse
                      : this.pluralEntityName
                    ).replace('-', '_'),
                  ),
                },
                {
                  name: AuthorizationHelper.getPermissionName(
                    permissionName,
                    (this.permissionEntityNameToUse
                      ? this.permissionEntityNameToUse
                      : this.pluralEntityName
                    ).replace('-', '_'),
                    true,
                  ),
                },
              );
            });
            hasPermissions = this.$authorizationService.hasPermissions(permissions);
          }
          actionConfig['activated'] =
            hasPermissions &&
            (this.config.data.activatedRowActions.indexOf(actionName) > -1 ||
              this.config.data.activatedRowMoreActions.indexOf(actionName) > -1);
          actionConfig['dropdown'] =
            this.config.data.activatedRowMoreActions.indexOf(actionName) > -1;

          if (actionName === 'posCheckout') {
            actionConfig['activated'] =
              hasPermissions &&
              this.config.data.activatedRowActions.indexOf(actionName) > -1 &&
              hasPosCheckoutPermission;
          }
        });

        if (this.rowActions.posCheckout?.show === true) {
          entity.actions['posCheckout'].show =
            entity.orders?.length > 0 &&
            entity.orders.some(
              order =>
                order.status === 'waiting' ||
                (order.type === 'attendee' && order.status === 'shopping_cart'),
            );
          // This condition is for articles that its don't have orders
          if ('in_pos' in entity) {
            entity.actions['posCheckout'].show = entity.in_pos === true;
          }
        }

        this.extendUpdateRowActions(entity);
      }
    }
  }

  extendUpdateRowActions(entity: any) {
    return null;
  }
  /**
   * Start Modal methods
   */

  onCloseModal() {
    this.showForm = false;
    this.$bvModal.hide('modal-create-' + this.entityName);
  }

  /**
   * End Modal methods
   */

  /**
   * Start Form methods
   */
  onNew() {
    if (!this.emitOnNew) {
      this.entity = null;
      this.showForm = true;
      // this.$bvModal.show('modal-resource-form');
      this.showDrawerSection = true;
      setTimeout(() => {
        this.disabledForm = false;
      });
    } else {
      this.$emit('on-new');
    }
  }

  setEntityAttributesAfterSave() {
    this.onNew();
  }

  onEdit(e, disabled = false) {
    if (!this.emitOnEdit) {
      console.log('clickOnEdit', e);
      this.entity = e.item;
      this.showForm = true;
      // this.$bvModal.show('modal-resource-form');
      this.showDrawerSection = true;
      setTimeout(() => {
        this.disabledForm = disabled;
      });
    } else {
      this.$emit('on-edit', e);
    }
  }

  onFormSave(next: string = '') {
    // this.isSavingForm = true;
    console.log('Saving');
    this.nextAction = next;
    this.$refs.stdForm.saveEntity();
  }

  onFormSaved($event: any = null) {
    if (this.nextAction === 'new') {
      this.setEntityAttributesAfterSave();
      this.$refs.provider.getEntities();
    } else if (this.nextAction === 'pay') {
      this.ordersAvailableToPosCheckout = $event.data?.orders;
      this.selectedOrdersToPosCheckout = $event.data?.orders;
      this.selectedContactIdToPosCheckout = $event.data?.contact_id;
      this.selectedPriceCategoryIdToPosCheckout = $event.data?.price_category_id;
      this.selectedInvoiceIdToPosCheckout =
        this.$store.getters['pos/currentTicket']?.status === 'to_pay'
          ? this.$store.getters['pos/currentTicket']
          : null;
      this.onFormClose();
      this.$refs.provider.getEntities();
      this.openExportPosCheckoutModal();
    } else if (this.nextAction === 'continue-editing') {
      this.$refs.provider.getEntities();
    } else {
      this.onFormClose();
      this.$refs.provider.getEntities();
    }
  }

  onFormSaveAndPay() {
    this.onFormSave('pay');
  }

  onFormClose() {
    this.showForm = false;
    // this.$bvModal.hide('modal-resource-form');
    // this.$refs.provider.getEntities();
    this.showDrawerSection = false;
    this.i++;
    this.isSavingForm = false;
  }

  onFormSaveAndNew() {
    // this.isSavingForm = true;
    this.nextAction = 'new';
    this.$refs.stdForm.saveEntity();
  }

  onFormEdit() {
    this.disabledForm = false;
  }

  onShow(e) {
    console.log('clickOnEdit', e);
  }

  onSelect(item: AnyEntity) {
    this.$emit('select', item);
  }

  onChangeSearchAndOrder($event) {
    this.searchAndOrderQueryParams = $event;
  }

  // TODO: Add functionality to onFormSaveAndEdit function in abstract-table
  onFormSaveAndEdit() {
    this.nextAction = 'edit';
    // this.formSave();
  }
  /** End form methods */

  /** Bulk form methods */

  onBulkEdit() {
    if (!this.emitOnBulkEdit) {
      this.entity = null;
      // this.$bvModal.show('modal-resource-form');
      this.showBulkEditForm = true;
      this.showBulkDrawerSection = true;
    } else {
      this.$emit('on-bulk-edit', this.selection);
    }
  }

  onFormBulkSave(next: string = '') {
    // this.isSavingForm = true;
    console.log('BulkSaving', this.selection);
    this.nextAction = next;
    this.$refs.stdBulkForm.formModel.ids = this.selection;
    this.$refs.stdBulkForm.saveEntity();
  }

  onFormBulkSaved($event: any = null) {
    this.onFormBulkClose();
    this.$refs.provider.getEntities();
  }

  onFormBulkClose() {
    this.showBulkEditForm = false;
    // this.$bvModal.hide('modal-resource-form');
    // this.$refs.provider.getEntities();
    this.showBulkDrawerSection = false;
    this.i++;
    this.isSavingBulkForm = false;
  }

  onBulkSavingEntity(event) {
    this.isSavingBulkForm = event;
  }

  /** End bulk form methods */

  setNextAction(action) {
    this.nextAction = action;
  }

  setConfig($event) {
    this.config = $event;
    this.updatePageActions();
    this.updateRowActions();
  }
  updateRowsActions() {
    this.updatePageActions();
    this.updateRowActions();
    this.getSelection();
  }

  // TODO - provisional
  onChangeStatusFilter(queryConfig, value) {
    this.$set(queryConfig.params, 'onlyTrashed', value);
    this.$refs.provider.getEntities();
    this.updatePageActions();
    this.updateRowActions();
  }
  onChangeFilter() {
    if (
      this.$refs.provider &&
      this.$refs.provider.canLoadEntities &&
      this.$refs.provider.firstLoadEntities
    ) {
      let changed = false;
      changed = this.setQueryConfigParams();
      if (changed) {
        // Only do the request if there was a change in the filters (to avoid duplicate request in the initialization of the filter component)
        // TODO: Try to hide parameters in route
        // if (this.filterConfigName) {
        //   let filterConfig = null;
        //   let filters = {};
        //   this.$bpcConfig
        //       .getEntityConfig(this.entityName)
        //       .subscribe(
        //           (res) => {
        //             filterConfig = res.data.filters.filterConfigName.fields;
        //             Object.entries(this.$refs.provider.queryConfig.params).forEach(([key, value]) => {
        //               if (key in filterConfig) {
        //                 if (filterConfig[key]['inputType'] !== 'hidden') {
        //                   filters[key] = value;
        //                 }
        //               } else {
        //                 filters[key] = value;
        //               }
        //               console.log('FILTERS', filters);
        //             });
        //           },
        //           error => {
        //             console.log(error);
        //           },
        //           () => {
        //           },
        //       );
        // }
        if (!this.ignoreURLParams) {
          this.$router.push({ query: this.$refs.provider.queryConfig.params }).catch(e => {
            return null;
          });
        }

        if (this.$refs.provider.pagination.current_page !== 1) {
          this.$set(this.$refs.provider.pagination, 'current_page', 1);
        } else {
          this.$refs.provider.getEntities();
          this.updatePageActions();
          this.updateRowActions();
        }
      }
    }
  }

  setQueryConfigParams(): boolean {
    const filterModel = this.$store.getters[`${this.storeName}/filterModel`](this.entityName);
    let changed = false;
    for (const key in filterModel) {
      if (this.$refs.provider.queryConfig.params[key] != filterModel[key]) {
        changed = true;
        this.$set(this.$refs.provider.queryConfig.params, key, filterModel[key]); // We set the queryConfig params of the provider
      }
    }
    return changed;
  }

  getModalTitle() {
    if (this.disabledForm) {
      const title = this.$t('global.models.' + this.entityName + '.name').toString();
      return title.charAt(0).toUpperCase() + title.slice(1); // capitalize
    } else if (this.entity && this.entity.id) {
      return (
        this.$t('global.modals.edit') + ' ' + this.$t('global.models.' + this.entityName + '.name')
      );
    } else {
      return (
        this.$t('global.modals.create') +
        ' ' +
        this.$t('global.models.' + this.entityName + '.name')
      );
    }
  }

  getGameModalTitle() {
    if (this.disabledForm) {
      const title = this.$t('global.models.' + this.entityName + '.game.name').toString();
      return title.charAt(0).toUpperCase() + title.slice(1); // capitalize
    } else if (this.entity && this.entity.id) {
      return (
        this.$t('global.modals.edit') +
        ' ' +
        this.$t('global.models.' + this.entityName + '.game.name')
      );
    } else {
      return (
        this.$t('global.modals.create') +
        ' ' +
        this.$t('global.models.' + this.entityName + '.game.name')
      );
    }
  }

  getMode(): string {
    return null;
  }

  onLoadQueryParamsFromURL($event) {
    if (!this.ignoreURLParams) {
      const params = Object.entries($event.params).filter(([k, v]) => {
        return !this.$refs.stdFilter.filterExcluded.includes(k);
      });
      const finalParams = Object.fromEntries(params);
      this.$store.commit(`${this.storeName}/setFilterModel`, {
        entity: this.entityName,
        model: finalParams,
      });
    }
    this.$store.commit(`${this.storeName}/stopLoadingUrlParameters`, this.entityName);
  }

  onExtendedFilterFormSearch(model = null) {
    const filterModel = model ? model : this.$refs.extendedFilter.filterModel;
    this.$store.commit(`${this.storeName}/setFilterModel`, {
      entity: this.entityName,
      model: filterModel,
    });

    this.onChangeFilter();

    if (this.isInput) {
      this.onExtendedFilterFormClose();
    }
  }

  @Watch('filterModel')
  onChangeFilterModel() {
    this.setQueryConfigParams();
  }

  onSavingEntity(event) {
    this.isSavingForm = event;
  }
  /**
   * If a link element form is saved, we update the table
   */
  tableEntitySaved() {
    this.$refs.provider.getEntities();
  }

  refreshEntitiesAfterOrdersAddedToPosCheckout() {
    this.$refs.provider.getEntities();
  }

  onLoadItems() {
    setTimeout(() => {
      if (this.$refs.entityTable) {
        this.$refs.entityTable.$on('on-click-display', $event => {
          if (this.displayFieldsToSubscribe.includes($event.field)) {
            this.onEdit($event, true);
          }
        });
      }
    });
  }

  // Function to get the display config of a specific field
  getFieldDisplayConfig(name: string): BPCDisplayConfigField {
    const fields = (this.$refs.entityTable?.fields || []) as BPCDisplayConfigField[];
    return fields.find(field => field.key === name);
  }

  exportCheckoutModalId = this.generateUUIDv4();

  generateUUIDv4() {
    return this.$bpcHelper.generateUUIDv4();
  }

  getLang() {
    const defaultLang = process.env.VUE_APP_DEFAULT_LANG || 'fr';
    return localStorage.getItem('lang') || this.$store.getters['auth/userLang'] || defaultLang;
  }
}
